import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Flag, Ban, CheckCircle2 } from "lucide-react";

export const REPORT_CATEGORIES = [
  "Fake account",
  "Incorrect license/credential",
  "Fraud",
  "Spam",
  "Harassment",
  "Inappropriate content",
  "Confidential information",
  "Patient information / PHI",
  "Incorrect shift information",
  "Other",
];

export default function ReportDialog({ targetType, targetId, label, blockUserId, blockPharmacyId, testIdPrefix = "report" }) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState(REPORT_CATEGORIES[0]);
  const [description, setDescription] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const blockTarget = async () => {
    setBusy(true);
    setError("");
    try {
      if (blockPharmacyId) await api.post(`/blocks/pharmacy/${blockPharmacyId}`);
      else await api.post("/blocks", { user_id: blockUserId });
      setMsg("Blocked. You will no longer be matched with this user.");
      setOpen(false);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/reports", {
        target_type: targetType,
        target_id: targetId,
        category,
        description,
      });
      setMsg(data.message);
      setOpen(false);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const block = async () => blockTarget();

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800"
          data-testid={`${testIdPrefix}-open`}
        >
          <Flag className="h-3.5 w-3.5" /> Report{label ? ` ${label}` : ""}
        </button>
        {msg && (
          <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700" data-testid={`${testIdPrefix}-success`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> {msg}
          </span>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 sm:items-center" data-testid={`${testIdPrefix}-dialog`}>
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
            <h3 className="font-display text-xl font-semibold">Report {label || "this listing"}</h3>
            <p className="mt-1 text-sm text-slate-600">
              Reports go to the FindRxStaff review team. Do not include patient information or PHI.
            </p>
            <label className="field-label mt-5">Reason</label>
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value)} data-testid={`${testIdPrefix}-category`}>
              {REPORT_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <label className="field-label mt-4">Details (optional)</label>
            <textarea
              className="field-input min-h-[90px]"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What happened? Keep it factual and free of confidential information."
              data-testid={`${testIdPrefix}-description`}
            />
            {error && <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div className="mt-5 flex flex-wrap gap-3">
              <button className="btn-primary" disabled={busy} onClick={submit} data-testid={`${testIdPrefix}-submit`}>
                {busy ? "Sending…" : "Submit report"}
              </button>
              {(blockUserId || blockPharmacyId) && (
                <button className="btn-ghost" disabled={busy} onClick={block} data-testid={`${testIdPrefix}-block`}>
                  <Ban className="h-4 w-4" /> Block user
                </button>
              )}
              <button className="btn-ghost" onClick={() => setOpen(false)} data-testid={`${testIdPrefix}-cancel`}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
